import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  Divider,
  Chip,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import SchoolIcon from '@mui/icons-material/School';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import GroupIcon from '@mui/icons-material/Group';
import HandshakeOutlined from '@mui/icons-material/HandshakeOutlined';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import { BRANCHES, SOURCES, LEAD_STATUSES, ADMISSION_STATUSES, VISA_STATUSES } from '../../config/constants';
import { updateStudent, addContactRemark } from '../../services/studentService';
import usePermissions from '../../hooks/usePermissions';

const formatCurrency = (amount) => {
  if (!amount && amount !== 0) return '-';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(amount);
};

const formatDate = (date) => {
  if (!date) return '-';
  return new Date(date).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const InfoRow = ({ label, value }) => (
  <Box sx={{ mb: 1.5 }}>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={500}>
      {value || '-'}
    </Typography>
  </Box>
);

const SectionCard = ({ title, icon, children, onEdit, editing }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        {icon}
        <Typography variant="subtitle1" fontWeight={600} sx={{ flex: 1 }}>
          {title}
        </Typography>
        {onEdit && !editing && (
          <IconButton size="small" onClick={onEdit} sx={{ color: 'primary.main' }}>
            <EditIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
      <Divider sx={{ mb: 2 }} />
      {children}
    </CardContent>
  </Card>
);

const StudentDetail = ({ student, onRefresh }) => {
  const { isAdmin, isManager, isConsultancy, canEditStudent } = usePermissions();
  const canEditCourseInfo = isAdmin || isManager || isConsultancy || canEditStudent;

  const [editingPersonal, setEditingPersonal] = useState(false);
  const [editingCourse, setEditingCourse] = useState(false);
  const [editingLoan, setEditingLoan] = useState(false);
  const [saving, setSaving] = useState(false);
  const [personalForm, setPersonalForm] = useState({});
  const [courseForm, setCourseForm] = useState({});
  const [loanForm, setLoanForm] = useState({});
  const [savingBranch, setSavingBranch] = useState(false);
  const [savingSource, setSavingSource] = useState(false);
  const [savingLeadStatus, setSavingLeadStatus] = useState(false);
  const [selectedLeadStatus, setSelectedLeadStatus] = useState(null);
  const [leadRemark, setLeadRemark] = useState('');

  if (!student) return null;

  const startEditCourse = () => {
    setCourseForm({
      course: student.course || '',
      university: student.university || '',
      country: student.country || '',
      intakeMonth: student.intakeMonth || '',
      intakeYear: student.intakeYear || '',
      courseDuration: student.courseDuration || '',
      courseFee: student.courseFee || '',
      admissionStatus: student.admissionStatus || '',
      i20CasNumber: student.i20CasNumber || '',
      visaStatus: student.visaStatus || '',
      scholarshipAmount: student.scholarshipAmount || '',
      courseRemarks: student.courseRemarks || '',
    });
    setEditingCourse(true);
  };

  const handleCourseChange = (field) => (e) => {
    setCourseForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const saveCourseInfo = async () => {
    setSaving(true);
    try {
      await updateStudent(student._id, courseForm);
      setEditingCourse(false);
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error('Failed to save course info:', err);
    } finally {
      setSaving(false);
    }
  };

  const startEditPersonal = () => {
    setPersonalForm({
      name: student.name || '',
      phone: student.phone || '',
      email: student.email || '',
      dob: student.dob ? student.dob.split('T')[0] : '',
      source: student.source || '',
    });
    setEditingPersonal(true);
  };

  const handlePersonalChange = (field) => (e) => {
    setPersonalForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const savePersonalInfo = async () => {
    setSaving(true);
    try {
      await updateStudent(student._id, personalForm);
      setEditingPersonal(false);
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error('Failed to save personal info:', err);
    } finally {
      setSaving(false);
    }
  };

  const startEditLoan = () => {
    setLoanForm({
      loanAmount: student.loanAmount || '',
      interestedBank: student.interestedBank || '',
      sanctionedAmount: student.sanctionedAmount || '',
      disbursedAmount: student.disbursedAmount || '',
    });
    setEditingLoan(true);
  };

  const handleLoanChange = (field) => (e) => {
    setLoanForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const saveLoanInfo = async () => {
    setSaving(true);
    try {
      await updateStudent(student._id, loanForm);
      setEditingLoan(false);
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error('Failed to save loan info:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleBranchChange = async (e) => {
    const newBranch = e.target.value;
    setSavingBranch(true);
    try {
      await updateStudent(student._id, { branch: newBranch });
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error('Failed to update branch:', err);
    } finally {
      setSavingBranch(false);
    }
  };

  const handleSourceChange = async (e) => {
    const newSource = e.target.value;
    setSavingSource(true);
    try {
      await updateStudent(student._id, { source: newSource });
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error('Failed to update source:', err);
    } finally {
      setSavingSource(false);
    }
  };

  const handleLeadStatusSelect = (e) => {
    setSelectedLeadStatus(e.target.value);
  };

  const saveLeadStatus = async () => {
    const newStatus = selectedLeadStatus || student.leadStatus || 'new_lead';
    const statusLabel = LEAD_STATUSES.find((s) => s.value === newStatus)?.label || newStatus;
    setSavingLeadStatus(true);
    try {
      await updateStudent(student._id, { leadStatus: newStatus });
      if (leadRemark.trim()) {
        const contactStatus = newStatus === 'interested' ? 'interested' : newStatus === 'not_interested' ? 'not_interested' : 'contacted';
        await addContactRemark(student._id, {
          contactStatus,
          remarks: `[${statusLabel}] ${leadRemark.trim()}`,
        });
      }
      setLeadRemark('');
      setSelectedLeadStatus(null);
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error('Failed to update lead status:', err);
    } finally {
      setSavingLeadStatus(false);
    }
  };

  const admissionLabel = ADMISSION_STATUSES.find((s) => s.value === student.admissionStatus)?.label || student.admissionStatus;
  const visaLabel = VISA_STATUSES.find((s) => s.value === student.visaStatus)?.label || student.visaStatus;

  return (
    <Grid container spacing={3}>
      {/* Personal Info */}
      <Grid size={{ xs: 12, md: 6 }}>
        <SectionCard
          title="Personal Information"
          icon={<PersonIcon color="primary" />}
          onEdit={canEditStudent ? startEditPersonal : undefined}
          editing={editingPersonal}
        >
          {editingPersonal ? (
            <Box>
              <Grid container spacing={2}>
                <Grid size={{ xs: 6 }}>
                  <TextField size="small" fullWidth label="Full Name" value={personalForm.name} onChange={handlePersonalChange('name')} />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <InfoRow label="File Number" value={student.fileNumber} />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <TextField size="small" fullWidth label="Phone" value={personalForm.phone} onChange={handlePersonalChange('phone')} />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <TextField size="small" fullWidth label="Email" value={personalForm.email} onChange={handlePersonalChange('email')} />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <TextField size="small" fullWidth label="Date of Birth" type="date" value={personalForm.dob} onChange={handlePersonalChange('dob')} slotProps={{ inputLabel: { shrink: true } }} />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <FormControl size="small" fullWidth>
                    <InputLabel>Source</InputLabel>
                    <Select value={personalForm.source} label="Source" onChange={handlePersonalChange('source')}>
                      <MenuItem value=""><em>Not Set</em></MenuItem>
                      {SOURCES.map((s) => (
                        <MenuItem key={s} value={s}>{s}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
                <Button size="small" startIcon={<CloseIcon />} onClick={() => setEditingPersonal(false)} disabled={saving}>Cancel</Button>
                <Button size="small" variant="contained" startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />} onClick={savePersonalInfo} disabled={saving}>
                  {saving ? 'Saving...' : 'Save'}
                </Button>
              </Box>
            </Box>
          ) : (
          <Grid container spacing={1}>
            <Grid size={{ xs: 6 }}>
              <InfoRow label="Full Name" value={student.name} />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <InfoRow label="File Number" value={student.fileNumber} />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <InfoRow label="Phone" value={student.phone} />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <InfoRow label="Email" value={student.email} />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <InfoRow label="Date of Birth" value={formatDate(student.dob)} />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <FormControl size="small" fullWidth>
                <InputLabel>Branch</InputLabel>
                <Select
                  value={student.branch || ''}
                  label="Branch"
                  onChange={handleBranchChange}
                  disabled={savingBranch}
                  startAdornment={<LocationCityIcon sx={{ fontSize: 16, mr: 0.5, color: 'text.secondary' }} />}
                >
                  <MenuItem value=""><em>Not Set</em></MenuItem>
                  {BRANCHES.map((b) => (
                    <MenuItem key={b} value={b}>{b}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 6 }}>
              <FormControl size="small" fullWidth>
                <InputLabel>Source</InputLabel>
                <Select
                  value={student.source || ''}
                  label="Source"
                  onChange={handleSourceChange}
                  disabled={savingSource}
                >
                  <MenuItem value=""><em>Not Set</em></MenuItem>
                  {SOURCES.map((s) => (
                    <MenuItem key={s} value={s}>{s}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 6 }}>
              <FormControl size="small" fullWidth>
                <InputLabel>Lead Status</InputLabel>
                <Select
                  value={selectedLeadStatus !== null ? selectedLeadStatus : (student.leadStatus || 'new_lead')}
                  label="Lead Status"
                  onChange={handleLeadStatusSelect}
                  disabled={savingLeadStatus}
                >
                  {LEAD_STATUSES.map((s) => (
                    <MenuItem key={s.value} value={s.value}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: s.color }} />
                        {s.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                size="small"
                fullWidth
                multiline
                rows={2}
                label="Lead Remark"
                placeholder="Why connected / interested / not interested..."
                value={leadRemark}
                onChange={(e) => setLeadRemark(e.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Button
                size="small"
                variant="contained"
                onClick={saveLeadStatus}
                disabled={savingLeadStatus}
                startIcon={savingLeadStatus ? <CircularProgress size={16} /> : <SaveIcon />}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                {savingLeadStatus ? 'Saving...' : 'Save Lead Status'}
              </Button>
            </Grid>
          </Grid>
          )}
        </SectionCard>
      </Grid>

      {/* Course Info */}
      <Grid size={{ xs: 12, md: 6 }}>
        <SectionCard
          title="Course Information"
          icon={<SchoolIcon color="secondary" />}
          onEdit={canEditCourseInfo ? startEditCourse : undefined}
          editing={editingCourse}
        >
          {editingCourse ? (
            <Box>
              <Grid container spacing={2}>
                <Grid size={{ xs: 6 }}>
                  <TextField size="small" fullWidth label="Course" value={courseForm.course} onChange={handleCourseChange('course')} />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <TextField size="small" fullWidth label="University" value={courseForm.university} onChange={handleCourseChange('university')} />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <TextField size="small" fullWidth label="Country" value={courseForm.country} onChange={handleCourseChange('country')} />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <TextField size="small" fullWidth label="Intake Month" value={courseForm.intakeMonth} onChange={handleCourseChange('intakeMonth')} />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <TextField size="small" fullWidth label="Intake Year" type="number" value={courseForm.intakeYear} onChange={handleCourseChange('intakeYear')} />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <TextField size="small" fullWidth label="Course Duration" value={courseForm.courseDuration} onChange={handleCourseChange('courseDuration')} placeholder="e.g. 2 Years" />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <TextField size="small" fullWidth label="Course Fee" type="number" value={courseForm.courseFee} onChange={handleCourseChange('courseFee')} slotProps={{ input: { startAdornment: <InputAdornment position="start">INR</InputAdornment> } }} />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <FormControl size="small" fullWidth>
                    <InputLabel>Admission Status</InputLabel>
                    <Select value={courseForm.admissionStatus} label="Admission Status" onChange={handleCourseChange('admissionStatus')}>
                      <MenuItem value=""><em>None</em></MenuItem>
                      {ADMISSION_STATUSES.map((s) => (
                        <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <TextField size="small" fullWidth label="I-20 / CAS Number" value={courseForm.i20CasNumber} onChange={handleCourseChange('i20CasNumber')} />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <FormControl size="small" fullWidth>
                    <InputLabel>Visa Status</InputLabel>
                    <Select value={courseForm.visaStatus} label="Visa Status" onChange={handleCourseChange('visaStatus')}>
                      <MenuItem value=""><em>None</em></MenuItem>
                      {VISA_STATUSES.map((s) => (
                        <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <TextField size="small" fullWidth label="Scholarship Amount" type="number" value={courseForm.scholarshipAmount} onChange={handleCourseChange('scholarshipAmount')} slotProps={{ input: { startAdornment: <InputAdornment position="start">INR</InputAdornment> } }} />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField size="small" fullWidth label="Course Remarks" multiline rows={2} value={courseForm.courseRemarks} onChange={handleCourseChange('courseRemarks')} />
                </Grid>
              </Grid>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
                <Button size="small" startIcon={<CloseIcon />} onClick={() => setEditingCourse(false)} disabled={saving}>
                  Cancel
                </Button>
                <Button size="small" variant="contained" startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />} onClick={saveCourseInfo} disabled={saving}>
                  {saving ? 'Saving...' : 'Save'}
                </Button>
              </Box>
            </Box>
          ) : (
            <Grid container spacing={1}>
              <Grid size={{ xs: 6 }}>
                <InfoRow label="Course" value={student.course} />
              </Grid>
              <Grid size={{ xs: 6 }}>
                <InfoRow label="University" value={student.university} />
              </Grid>
              <Grid size={{ xs: 6 }}>
                <InfoRow label="Country" value={student.country} />
              </Grid>
              <Grid size={{ xs: 6 }}>
                <InfoRow label="Intake" value={student.intakeMonth ? `${student.intakeMonth}${student.intakeYear ? ` ${student.intakeYear}` : ''}` : '-'} />
              </Grid>
              <Grid size={{ xs: 6 }}>
                <InfoRow label="Course Duration" value={student.courseDuration} />
              </Grid>
              <Grid size={{ xs: 6 }}>
                <InfoRow label="Course Fee" value={formatCurrency(student.courseFee)} />
              </Grid>
              <Grid size={{ xs: 6 }}>
                <InfoRow label="Admission Status" value={admissionLabel} />
              </Grid>
              <Grid size={{ xs: 6 }}>
                <InfoRow label="I-20 / CAS Number" value={student.i20CasNumber} />
              </Grid>
              <Grid size={{ xs: 6 }}>
                <InfoRow label="Visa Status" value={visaLabel} />
              </Grid>
              <Grid size={{ xs: 6 }}>
                <InfoRow label="Scholarship" value={formatCurrency(student.scholarshipAmount)} />
              </Grid>
              {student.courseRemarks && (
                <Grid size={{ xs: 12 }}>
                  <InfoRow label="Course Remarks" value={student.courseRemarks} />
                </Grid>
              )}
            </Grid>
          )}
        </SectionCard>
      </Grid>

      {/* Loan Info */}
      <Grid size={{ xs: 12, md: 6 }}>
        <SectionCard
          title="Loan Information"
          icon={<AccountBalanceIcon sx={{ color: '#7b1fa2' }} />}
          onEdit={canEditStudent ? startEditLoan : undefined}
          editing={editingLoan}
        >
          {editingLoan ? (
            <Box>
              <Grid container spacing={2}>
                <Grid size={{ xs: 6 }}>
                  <TextField size="small" fullWidth label="Loan Amount" type="number" value={loanForm.loanAmount} onChange={handleLoanChange('loanAmount')} slotProps={{ input: { startAdornment: <InputAdornment position="start">INR</InputAdornment> } }} />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <TextField size="small" fullWidth label="Interested Bank" value={loanForm.interestedBank} onChange={handleLoanChange('interestedBank')} />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <TextField size="small" fullWidth label="Sanctioned Amount" type="number" value={loanForm.sanctionedAmount} onChange={handleLoanChange('sanctionedAmount')} slotProps={{ input: { startAdornment: <InputAdornment position="start">INR</InputAdornment> } }} />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <TextField size="small" fullWidth label="Disbursed Amount" type="number" value={loanForm.disbursedAmount} onChange={handleLoanChange('disbursedAmount')} slotProps={{ input: { startAdornment: <InputAdornment position="start">INR</InputAdornment> } }} />
                </Grid>
              </Grid>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
                <Button size="small" startIcon={<CloseIcon />} onClick={() => setEditingLoan(false)} disabled={saving}>Cancel</Button>
                <Button size="small" variant="contained" startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />} onClick={saveLoanInfo} disabled={saving}>
                  {saving ? 'Saving...' : 'Save'}
                </Button>
              </Box>
            </Box>
          ) : (
          <Grid container spacing={1}>
            <Grid size={{ xs: 6 }}>
              <InfoRow
                label="Loan Amount"
                value={formatCurrency(student.loanAmount)}
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <InfoRow label="Interested Bank" value={student.interestedBank} />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <InfoRow
                label="Sanctioned Amount"
                value={formatCurrency(student.sanctionedAmount)}
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <InfoRow
                label="Disbursed Amount"
                value={formatCurrency(student.disbursedAmount)}
              />
            </Grid>
            {student.consultancyId && (
              <Grid size={{ xs: 12 }}>
                <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <HandshakeOutlined sx={{ fontSize: 16, color: '#0EA5E9' }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Consultancy
                    </Typography>
                    <Typography variant="body2" fontWeight={600} sx={{ color: '#0EA5E9' }}>
                      {student.consultancyId?.name || student.consultancyId}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            )}
          </Grid>
          )}
        </SectionCard>
      </Grid>

      {/* Assignment Info - hidden for consultancy */}
      {!isConsultancy && (
        <Grid size={{ xs: 12, md: 6 }}>
          <SectionCard
            title="Team Assignment"
            icon={<GroupIcon sx={{ color: '#2e7d32' }} />}
          >
            <Grid container spacing={1}>
              <Grid size={{ xs: 12 }}>
                <InfoRow
                  label="Manager"
                  value={student.managerId?.name || student.managerName}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <InfoRow
                  label="Associate"
                  value={student.associateId?.name || student.associateName}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <InfoRow
                  label="Banker"
                  value={student.bankerId?.name || student.bankerName}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                    Created
                  </Typography>
                  <Chip
                    label={formatDate(student.createdAt)}
                    size="small"
                    variant="outlined"
                  />
                </Box>
              </Grid>
            </Grid>
          </SectionCard>
        </Grid>
      )}
    </Grid>
  );
};

export default StudentDetail;
